<template>
    <GLText
        ref="refText"
        :text="newText"
        :layer="layer"
        :options="textOptions"
        @sync="onTextSync"
        :material="material"
        name="Category"
    ></GLText>
</template>

<script setup>
    import { Mesh, MeshMatcapMaterial } from 'three';
    import { computed, onMounted, reactive, ref, watch, watchEffect } from 'vue';

    import glslDraw from '@resn/gozer-glsl/shapes/draw.glsl';
    import glslRoundedRect from '@resn/gozer-glsl/shapes/round-rect.glsl';
    import { simpleMaterial, simplePlaneGeometry } from '@resn/gozer-three';
    import { CustomMaterial } from '@resn/gozer-three/materials';
    import { usePane, useThreeObject } from '@resn/gozer-vue';

    import { setupCategoryTextMaterial } from '../../materials/CategoryTextMaterial';
    import GLText from '~/components/gl/GLText.vue';
    import { useModelExporter } from '~/composables/useModelExporter';
    import { LAYER_SCENE } from '~/core/constants';
    import { useGlobalAssets } from '~/providers/GlobalAssets';

    const props = defineProps({
        debug: { default: false },
        layer: { default: LAYER_SCENE },
        text: { default: 'Category' },
    });

    const newText = ref(props.text);
    const refText = ref(null);
    const slantAngle = ref(0.3);

    const textOptions = reactive({
        fontSize: 0.05,
        color: 'black',
        fontStyle: 'italic',
    });

    const material = {
        fn: setupCategoryTextMaterial,
        uniforms: {
            uSlantAngle: { value: slantAngle.value },
        },
    };

    const py = ref(-0.15);
    const pz = ref(0.2);
    const s = ref(1);
    const _props = { pz, py, s };
    const { object } = useThreeObject(null, { name: 'Category', props: _props });

    const bg = new Mesh(
        simplePlaneGeometry,
        // simpleMaterial()
        new CustomMaterial({
            options: {
                name: 'CategoryBg',
            },
            uniforms: {
                uAspect: 1,
                uSlantAngle: slantAngle.value,
            },
            vs: `
            uniform float uSlantAngle;
            varying vec2 vUv;
            void main() {
                vUv = uv;

                vec3 pos = position;
                // float slantOffset = pos.y * tan(uSlantAngle);
                // pos.x += slantOffset;

                gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
            }
        `,
            fs: `
            uniform float uSlantAngle;
            uniform float uAspect;
            
            varying vec2 vUv;
            
            ${glslDraw}
            ${glslRoundedRect}

            void main() {
                vec2 newUv = vUv;
                float slantAngle = uSlantAngle * 0.8;
                float slantOffset = newUv.y * tan(slantAngle);
                newUv.x -= slantOffset;
                newUv.x += slantAngle * 0.5;

                vec3 white = vec3(1.0);

                float rect = roundrect(newUv, vec2(1.0, 1.0 / uAspect) * 1.5, 0.1);
                gl_FragColor = vec4(white, rect);
            }
        `,
        })
    );
    bg.layers.set(props.layer);
    bg.position.set(0, 0, -0.001);
    object.add(bg);

    useGlobalAssets(({ data }) => {
        bg.material.matcap = data.matcapPackWhite;
    });

    const realSize = computed(() => refText.value?.realSize);
    const scale = 1.6;
    watchEffect(() => {
        if (realSize.value) {
            const { width, height } = realSize.value;
            const aspect = (width / height) * 1.1;

            const largest = Math.max(width, height);
            bg.scale.set(largest * scale, largest * scale, 1);
            // bg.scale.set(realSize.value.width + padding * 3, realSize.value.height + padding, 1);
            bg.position.y = height * 0.06;

            bg.material.uniforms.uAspect.value = aspect;
        }
    });

    const onTextSync = (e) => {
        updateLetters();
    };

    const updateLetters = () => refText.value.updateLetters();

    watch(
        () => props.text,
        (v) => (newText.value = v)
    );

    watch(slantAngle, (val) => {
        const { material } = refText.value.mesh;
        material.uSlantAngle = val;
        bg.material.uSlantAngle = val;
    });

    const modelExport = useModelExporter();

    if (!modelExport) {
        // usePane(
        //     [
        //         // { name: 'py', value: py, options: { min: -1, max: 1, step: 0.01 } },
        //         // { name: 'pz', value: pz, options: { min: -1, max: 1, step: 0.01 } },
        //         // { name: 's', value: s, options: { min: 0.5, max: 1.5, step: 0.01 } },
        //         // { name: 'slantAngle', value: slantAngle, options: { min: 0, max: 1, step: 0.01 } },
        //         { name: 'text', value: newText },
        //     ],
        //     { title: 'Category' }
        // );
    }

    defineExpose({});
</script>
